import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../../components/ui/sheet';
import { Checkbox } from '../../components/ui/checkbox';
import { LeadFiltersDialog } from './LeadFilters';
import { RescoreLeadsButton } from './RescoreLeadsButton';
import { Plus, Search, LayoutGrid, Table as TableIcon, X, Settings, Upload } from 'lucide-react';

export function LeadToolbar({
  search, setSearch, setCurrentPage,
  settingsOpen, setSettingsOpen,
  pipelineStages, visibleStatuses, setVisibleStatuses,
  importSheetOpen, setImportSheetOpen,
  filterOpen, setFilterOpen,
  filters, setFilters, sortBy, setSortBy,
  leadSources, teamMembers, products,
  view, setView,
  hasPermission, resetForm, setDialogOpen,
  discoverOpen, setDiscoverOpen,
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 sm:gap-3" data-testid="leads-header">
      <div className="shrink-0">
        <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">Leads</h1>
      </div>

      {/* Search */}
      <div className="relative w-full sm:max-w-[220px] sm:flex-1 order-3 sm:order-none">
        <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <Input
          placeholder="Search..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
          className="pl-8 pr-8 h-9 text-sm"
          data-testid="lead-search-input"
        />
        {search && (
          <button
            onClick={() => { setSearch(''); setCurrentPage(1); }}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
            data-testid="lead-search-clear"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <div className="flex items-center gap-1.5 ml-auto flex-wrap">
        {/* Settings */}
        <Sheet open={settingsOpen} onOpenChange={setSettingsOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="settings-button">
              <Settings className="h-4 w-4" />
            </Button>
          </SheetTrigger>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>Kanban Settings</SheetTitle>
            </SheetHeader>
            <div className="mt-6 space-y-4">
              <div>
                <h3 className="text-sm font-medium mb-3">Visible Status Columns</h3>
                <p className="text-xs text-muted-foreground mb-4">
                  Select which stage columns to display on the Kanban board
                </p>
                <div className="space-y-3">
                  {pipelineStages.filter((status) => status.visible !== false).map((status) => (
                    <div key={status.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`status-${status.id}`}
                        checked={visibleStatuses.includes(status.name)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setVisibleStatuses([...visibleStatuses, status.name]);
                          } else {
                            setVisibleStatuses(visibleStatuses.filter(s => s !== status.name));
                          }
                        }}
                      />
                      <label
                        htmlFor={`status-${status.id}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                      >
                        {status.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </SheetContent>
        </Sheet>

        {/* Import */}
        {hasPermission('lead', 'kanban', 'create') && (
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setImportSheetOpen(true)} data-testid="import-leads-button">
            <Upload className="h-4 w-4" />
          </Button>
        )}

        {/* Re-score all leads with AI (product segmentation) */}
        {hasPermission('lead', 'kanban', 'edit') && <RescoreLeadsButton />}

        {/* Filter */}
        <LeadFiltersDialog
          filterOpen={filterOpen}
          setFilterOpen={setFilterOpen}
          filters={filters}
          setFilters={setFilters}
          sortBy={sortBy}
          setSortBy={setSortBy}
          pipelineStages={pipelineStages}
          leadSources={leadSources}
          teamMembers={teamMembers}
          products={products}
        />

        {/* View Toggle */}
        <div className="flex items-center border border-border rounded-md overflow-hidden">
          <Button
            variant={view === 'kanban' ? 'default' : 'ghost'}
            size="icon"
            className="h-8 w-8 rounded-none"
            onClick={() => { setView('kanban'); setCurrentPage(1); }}
            data-testid="kanban-view-toggle"
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            variant={view === 'table' ? 'default' : 'ghost'}
            size="icon"
            className="h-8 w-8 rounded-none"
            onClick={() => { setView('table'); setCurrentPage(1); }}
            data-testid="table-view-toggle"
          >
            <TableIcon className="h-4 w-4" />
          </Button>
        </div>

        {/* Add Lead */}
        {hasPermission('lead', 'kanban', 'create') && (
          <Button size="sm" className="h-8 px-2 sm:px-3" onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="create-lead-button">
            <Plus className="h-4 w-4 sm:mr-1" />
            <span className="hidden sm:inline">Add Lead</span>
          </Button>
        )}
      </div>
    </div>
  );
}
